import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, fmtMoney, fmtRoas, todayStr } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScoreRing, StageBadge, VerdictBadge, Spinner, SectionTitle, EmptyState, roasTone } from '@/components/common';
import { CreativeDialog } from '@/components/CreativeDialog';
import { MetricDialog } from '@/components/MetricDialog';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Trash2, Pencil, AlertTriangle, ChevronDown, Plus, Zap, Film, LineChart as LineChartIcon, ListTodo } from 'lucide-react';
import { cn } from '@/lib/utils';

const CRITERION_TONE = (score) => (score >= 7 ? 'bg-emerald-500' : score >= 5 ? 'bg-amber-500' : 'bg-rose-500');

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { stagesData } = useApp();
  const [product, setProduct] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [creativeOpen, setCreativeOpen] = useState(false);
  const [editCreative, setEditCreative] = useState(null);
  const [metricOpen, setMetricOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [expandedDiag, setExpandedDiag] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [p, c, m, t] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/creatives?product_id=${id}`),
        api.get(`/metrics?product_id=${id}&days=60`),
        api.get(`/tasks?product_id=${id}`),
      ]);
      setProduct(p.data);
      setNotesDraft(p.data.research_notes || '');
      setCreatives(c.data);
      setMetrics(m.data);
      setTasks(t.data);
    } catch (e) {
      if (e.response?.status === 404) navigate('/pipeline');
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // poll while AI jobs running
  useEffect(() => {
    const busy = product && (product.score_status === 'scoring' || product.strategy_status === 'generating' ||
      metrics.some((m) => m.diagnosis_status === 'pending') || creatives.some((c) => c.diagnosis_status === 'diagnosing'));
    clearInterval(pollRef.current);
    if (busy) pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [product, metrics, creatives, load]);

  // Derived once per creatives change; must stay above the early return (rules of hooks)
  const diagnosedCreatives = useMemo(() => creatives.filter((c) => c.ai_diagnosis), [creatives]);

  if (!product) return <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>;

  const margin = product.sell_price != null && product.source_price != null
    ? product.sell_price - product.source_price - (product.shipping_cost || 0) : null;
  const stageDef = (stagesData.stages || []).find((s) => s.key === product.stage);

  const runScore = async () => {
    await api.post(`/products/${id}/score`);
    setProduct((p) => ({ ...p, score_status: 'scoring' }));
    toast.info('AI analysis running — usually takes 30-90 seconds');
  };

  const runStrategy = async () => {
    await api.post(`/products/${id}/strategy`);
    setProduct((p) => ({ ...p, strategy_status: 'generating' }));
    toast.info('Generating strategy — avatars, angles, hooks, offers...');
  };

  const moveStage = async (stage) => {
    const { data } = await api.put(`/products/${id}/stage`, { stage });
    if (data.automation === 'roadmap_created') toast.success('Qualified! Test roadmap + starter tasks auto-created', { duration: 5000 });
    load();
  };

  const saveEdit = async () => {
    const payload = { ...editForm };
    ['source_price', 'sell_price', 'shipping_cost'].forEach((k) => {
      if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      else payload[k] = Number(payload[k]);
    });
    await api.put(`/products/${id}`, payload);
    toast.success('Product updated');
    setEditOpen(false);
    load();
  };

  const delProduct = async () => {
    await api.delete(`/products/${id}`);
    toast.success('Product deleted');
    navigate('/pipeline');
  };

  const toggleRoadmap = async (item) => {
    setProduct((p) => ({ ...p, roadmap: p.roadmap.map((r) => (r.id === item.id ? { ...r, done: !item.done } : r)) }));
    await api.put(`/products/${id}/roadmap/${item.id}`, { done: !item.done });
  };

  const genRoadmap = async () => {
    const { data } = await api.post(`/products/${id}/roadmap/generate`);
    setProduct(data);
    toast.success('Roadmap generated');
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    await api.post('/tasks', { title: taskTitle.trim(), product_id: id, category: 'Research', due_date: todayStr() });
    setTaskTitle('');
    load();
  };

  const toggleTask = async (t) => {
    await api.put(`/tasks/${t.id}`, { done: !t.done });
    load();
  };

  const saveNotes = async () => {
    await api.put(`/products/${id}`, { research_notes: notesDraft });
    toast.success('Notes saved');
  };

  const diagnoseCreative = async (c) => {
    await api.post(`/creatives/${c.id}/diagnose`);
    setCreatives((cs) => cs.map((x) => (x.id === c.id ? { ...x, diagnosis_status: 'diagnosing' } : x)));
    toast.info('AI diagnosing creative...');
  };

  const roadmapSections = {};
  (product.roadmap || []).forEach((r) => { (roadmapSections[r.section] = roadmapSections[r.section] || []).push(r); });
  const roadmapDone = (product.roadmap || []).filter((r) => r.done).length;
  const roadmapTotal = (product.roadmap || []).length;

  const score = product.score;
  const strategy = product.strategy;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {score ? <ScoreRing score={score.total_score} /> : (
            <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border border-dashed text-xs text-muted-foreground">
              {product.score_status === 'scoring' ? <Spinner /> : 'no score'}
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 data-testid="product-detail-name" className="text-2xl font-bold tracking-tight">{product.name}</h1>
              {score && <VerdictBadge verdict={score.verdict} />}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <StageBadge stage={product.stage} stages={stagesData.stages} />
              {margin != null && <span className="font-mono tabular-nums">margin {fmtMoney(margin)} / unit · break-even CPA {fmtMoney(margin)}</span>}
            </div>
            {product.next_action && (
              <p className="mt-2 flex items-center gap-1.5 text-sm"><Zap className="h-3.5 w-3.5 text-primary" /><span className="font-medium">Next:</span> {product.next_action}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={product.stage} onValueChange={moveStage}>
            <SelectTrigger data-testid="product-detail-stage-select" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(stagesData.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="product-detail-edit-button" variant="outline" size="icon" aria-label="Edit product" onClick={() => { setEditForm({ name: product.name, description: product.description, source_price: product.source_price ?? '', sell_price: product.sell_price ?? '', shipping_cost: product.shipping_cost ?? '', supplier_url: product.supplier_url, product_url: product.product_url, image_url: product.image_url }); setEditOpen(true); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="product-detail-delete-button" variant="outline" size="icon" aria-label="Delete product"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {product.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes its roadmap, tasks, metrics, creatives and AI analysis. Linked notes are kept but unlinked, and your logged time history stays intact. This can't be undone.
                  {['live_testing', 'optimizing', 'scaling'].includes(product.stage) && (
                    <span className="mt-2 block font-medium text-amber-600 dark:text-amber-400">Heads up: this product is in an active test. If the test simply ended, move it to Killed instead — Delete erases the record entirely.</span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="product-detail-delete-confirm" onClick={delProduct} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Stage guide */}
      {stageDef && (
        <Card className="border-primary/20">
          <CardContent className="p-4 text-sm">
            <p><span className="font-semibold">{stageDef.label} stage · exit when:</span> {stageDef.exit_criteria}</p>
            <p className="mt-1 text-xs text-muted-foreground"><Sparkles className="mr-1 inline h-3 w-3 text-primary" />{stageDef.ai_tip}</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="analysis" data-testid="product-detail-tabs">
        <TabsList className="flex h-auto w-full flex-wrap justify-start">
          {['analysis', 'strategy', 'roadmap', 'creatives', 'metrics', 'tasks', 'notes'].map((t) => (
            <TabsTrigger key={t} data-testid={`product-detail-tab-${t}`} value={t} className="capitalize">{t === 'analysis' ? 'AI Analysis' : t === 'strategy' ? 'AI Strategy' : t}</TabsTrigger>
          ))}
        </TabsList>

        {/* ---- AI ANALYSIS ---- */}
        <TabsContent value="analysis" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <SectionTitle>AI Product Analysis — 15 weighted criteria</SectionTitle>
            <Button data-testid="product-run-analysis-button" size="sm" onClick={runScore} disabled={product.score_status === 'scoring'} className="accent-btn gap-2">
              {product.score_status === 'scoring' ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
              {score ? 'Re-run Analysis' : 'Run AI Analysis'}
            </Button>
          </div>
          {product.score_status === 'scoring' && (
            <Card><CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
              <Spinner /> Scoring across problem intensity, demand, margin, competition, compliance and 10 more criteria... (~30-90s)
            </CardContent></Card>
          )}
          {product.score_status === 'error' && (
            <Card className="border-rose-500/30"><CardContent className="p-5 text-sm">
              <p className="flex items-center gap-2 text-rose-500"><AlertTriangle className="h-4 w-4" /> Analysis failed: {product.score_error}</p>
              <p className="mt-1 text-xs text-muted-foreground">Hit Run AI Analysis to retry.</p>
            </CardContent></Card>
          )}
          {score ? (
            <>
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm font-medium">{score.verdict_reasoning}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Top strengths</p>
                      <ul className="list-disc space-y-1 pl-4 text-sm">{(score.top_strengths || []).map((s) => <li key={s}>{s}</li>)}</ul>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Top risks</p>
                      <ul className="list-disc space-y-1 pl-4 text-sm">{(score.top_risks || []).map((s) => <li key={s}>{s}</li>)}</ul>
                    </div>
                  </div>
                  <p className="mt-4 rounded-lg bg-primary/10 p-3 text-sm"><Zap className="mr-1 inline h-3.5 w-3.5 text-primary" /><span className="font-semibold">Next best action:</span> {score.next_best_action}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="space-y-3">
                    {(score.criteria || []).map((c) => (
                      <div key={c.key} data-testid="score-criterion-row">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{c.label}</span>
                          <span className="font-mono tabular-nums text-muted-foreground">{c.score}/10 <span className="text-xs">· w {Math.round(c.weight * 100)}%</span></span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div className={cn('h-full rounded-full', CRITERION_TONE(c.score))} style={{ width: `${c.score * 10}%` }} />
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{c.reasoning}</p>
                        {c.red_flags?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.red_flags.map((f) => (
                              <span key={f} className="rounded bg-rose-500/10 px-1.5 py-0.5 text-[11px] text-rose-600 dark:text-rose-400"><AlertTriangle className="mr-0.5 inline h-3 w-3" />{f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : product.score_status === 'none' && (
            <EmptyState testId="analysis-empty" icon={Sparkles} title="Not analyzed yet"
              subtitle="The AI scores this product 1-100 across 15 weighted criteria and gives a TEST / MAYBE / SKIP verdict. Add pricing first for an accurate margin read."
              action={<Button data-testid="analysis-empty-run-button" onClick={runScore} className="gap-2"><Sparkles className="h-4 w-4" />Run AI Analysis</Button>} />
          )}
        </TabsContent>

        {/* ---- AI STRATEGY ---- */}
        <TabsContent value="strategy" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <SectionTitle>AI Strategy Engine — avatars, angles, hooks, offers</SectionTitle>
            <Button data-testid="product-generate-strategy-button" size="sm" onClick={runStrategy} disabled={product.strategy_status === 'generating'} className="accent-btn gap-2">
              {product.strategy_status === 'generating' ? <Spinner /> : <Sparkles className="h-4 w-4" />}
              {strategy ? 'Regenerate Strategy' : 'Generate Strategy'}
            </Button>
          </div>
          {product.strategy_status === 'generating' && (
            <Card><CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
              <Spinner /> Building avatars, angles, hooks, offers, budget and kill/scale rules... (~30-90s)
            </CardContent></Card>
          )}
          {product.strategy_status === 'error' && (
            <Card className="border-rose-500/30"><CardContent className="p-5 text-sm text-rose-500">Strategy generation failed: {product.strategy_error}. Retry above.</CardContent></Card>
          )}
          {strategy ? (
            <div className="space-y-4">
              <div>
                <SectionTitle>Avatars</SectionTitle>
                <div className="grid gap-3 md:grid-cols-3">
                  {(strategy.avatars || []).map((a) => (
                    <Card key={a.name} data-testid="strategy-avatar-card"><CardContent className="p-4">
                      <p className="text-sm font-semibold">{a.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{a.description}</p>
                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Pains</p>
                      <ul className="list-disc pl-4 text-xs">{(a.pain_points || []).map((p) => <li key={p}>{p}</li>)}</ul>
                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Desires</p>
                      <ul className="list-disc pl-4 text-xs">{(a.desires || []).map((p) => <li key={p}>{p}</li>)}</ul>
                      <p className="mt-2 text-xs text-muted-foreground">{a.demographics} · {a.awareness_level}</p>
                    </CardContent></Card>
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle>Angles & Hooks</SectionTitle>
                <div className="grid gap-3 md:grid-cols-2">
                  {(strategy.angles || []).map((a) => (
                    <Card key={a.name} data-testid="strategy-angle-card"><CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{a.name}</p>
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase">{a.emotion}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{a.description} · for: {a.avatar}</p>
                      <div className="mt-2 space-y-1">
                        {(a.hook_ideas || []).map((h) => (
                          <p key={h} className="rounded bg-muted/60 px-2 py-1 text-xs">“{h}”</p>
                        ))}
                      </div>
                    </CardContent></Card>
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle>Offers</SectionTitle>
                <div className="grid gap-3 md:grid-cols-3">
                  {(strategy.offers || []).map((o) => (
                    <Card key={o.name}><CardContent className="p-4">
                      <p className="text-sm font-semibold">{o.name}</p>
                      <p className="mt-1 text-xs">{o.structure}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{o.why_it_works}</p>
                    </CardContent></Card>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Card><CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Landing page strategy</p>
                  <p className="mt-1 text-sm">{strategy.landing_page_strategy}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Testing strategy</p>
                  <p className="mt-1 text-sm">{strategy.testing_strategy}</p>
                  <p className="mt-2 text-sm"><span className="font-semibold">Initial budget:</span> {strategy.initial_daily_budget}</p>
                </CardContent></Card>
              </div>
              <Card><CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Creative concepts to produce</p>
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">{(strategy.creative_concepts || []).map((c) => <li key={c}>{c}</li>)}</ul>
              </CardContent></Card>
              <div className="grid gap-3 md:grid-cols-2">
                <Card className="border-rose-500/25"><CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Kill criteria</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">{(strategy.kill_criteria || []).map((c) => <li key={c}>{c}</li>)}</ul>
                </CardContent></Card>
                <Card className="border-emerald-500/25"><CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Scale criteria</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">{(strategy.scale_criteria || []).map((c) => <li key={c}>{c}</li>)}</ul>
                </CardContent></Card>
              </div>
              <p className="rounded-lg bg-primary/10 p-3 text-sm"><Zap className="mr-1 inline h-3.5 w-3.5 text-primary" /><span className="font-semibold">Next best action:</span> {strategy.next_best_action}</p>
            </div>
          ) : product.strategy_status === 'none' && (
            <EmptyState testId="strategy-empty" icon={Sparkles} title="No strategy yet"
              subtitle="Generate 3 avatars, 4 angles with hooks, 3 offers, landing page + testing plan, budget and kill/scale rules for this product."
              action={<Button data-testid="strategy-empty-generate-button" onClick={runStrategy} className="gap-2"><Sparkles className="h-4 w-4" />Generate Strategy</Button>} />
          )}
        </TabsContent>

        {/* ---- ROADMAP ---- */}
        <TabsContent value="roadmap" className="mt-4 space-y-4">
          {roadmapTotal === 0 ? (
            <EmptyState testId="roadmap-empty" icon={ListTodo} title="No roadmap yet"
              subtitle="The roadmap is a proven 9-checklist path: research, supplier, store, product page, offer, creative, launch, optimization, kill/scale. It's auto-created when you qualify a product, or generate it now."
              action={<Button data-testid="roadmap-generate-button" onClick={genRoadmap} className="gap-2"><Plus className="h-4 w-4" />Generate Roadmap</Button>} />
          ) : (
            <>
              <Card><CardContent className="p-4">
                <div className="flex items-center justify-between text-sm">
                  <p className="font-semibold">Test roadmap progress</p>
                  <p className="font-mono tabular-nums">{roadmapDone}/{roadmapTotal}</p>
                </div>
                <Progress value={(roadmapDone / roadmapTotal) * 100} className="mt-2 h-2" />
              </CardContent></Card>
              {Object.entries(roadmapSections).map(([section, items]) => {
                const done = items.filter((i) => i.done).length;
                return (
                  <Card key={section} data-testid="roadmap-section-card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{section}</p>
                        <span className="font-mono text-xs tabular-nums text-muted-foreground">{done}/{items.length}</span>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        {items.map((item) => (
                          <label key={item.id} className="flex cursor-pointer items-start gap-2 text-sm">
                            <Checkbox data-testid="roadmap-item-checkbox" checked={item.done} onCheckedChange={() => toggleRoadmap(item)} className="mt-0.5" />
                            <span className={cn(item.done && 'text-muted-foreground line-through')}>{item.item}</span>
                          </label>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </TabsContent>

        {/* ---- CREATIVES ---- */}
        <TabsContent value="creatives" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <SectionTitle>Creatives for this product</SectionTitle>
            <Button data-testid="product-add-creative-button" size="sm" onClick={() => { setEditCreative(null); setCreativeOpen(true); }} className="gap-2"><Plus className="h-4 w-4" />Add Creative</Button>
          </div>
          {creatives.length === 0 ? (
            <EmptyState testId="product-creatives-empty" icon={Film} title="No creatives yet" subtitle="Upload or link your ads here, tagged by avatar / angle / hook, and track their numbers." />
          ) : (
            <Card>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Angle</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Spend</TableHead><TableHead className="text-right">CTR</TableHead>
                  <TableHead className="text-right">ROAS</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {creatives.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => { setEditCreative(c); setCreativeOpen(true); }}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.angle || '—'}</TableCell>
                      <TableCell><span className="rounded bg-secondary px-1.5 py-0.5 text-xs">{c.status}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(c.spend)}</TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">{c.ctr}%</TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">{fmtRoas(c.roas)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button variant="outline" size="sm" className="gap-1" disabled={c.diagnosis_status === 'diagnosing'} onClick={() => diagnoseCreative(c)}>
                          {c.diagnosis_status === 'diagnosing' ? <Spinner /> : <Sparkles className="h-3.5 w-3.5" />} AI
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          {diagnosedCreatives.map((c) => (
            <Card key={c.id} className="border-primary/20"><CardContent className="p-4 text-sm">
              <p className="font-semibold">{c.name} — AI verdict: <span className="text-primary">{c.ai_diagnosis.verdict}</span></p>
              <p className="mt-1">{c.ai_diagnosis.diagnosis}</p>
              <p className="mt-1 text-xs"><span className="font-semibold">Next:</span> {c.ai_diagnosis.next_action}</p>
            </CardContent></Card>
          ))}
        </TabsContent>

        {/* ---- METRICS ---- */}
        <TabsContent value="metrics" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <SectionTitle>Daily metrics</SectionTitle>
            <Button data-testid="product-log-metrics-button" size="sm" onClick={() => setMetricOpen(true)} className="gap-2"><Plus className="h-4 w-4" />Log Day</Button>
          </div>
          {metrics.length === 0 ? (
            <EmptyState testId="product-metrics-empty" icon={LineChartIcon} title="No metrics logged" subtitle="Log spend, revenue and funnel numbers daily during testing. The AI diagnoses each day automatically." />
          ) : (
            <Card>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">ROAS</TableHead><TableHead className="text-right">CPA</TableHead>
                  <TableHead className="text-right">Profit</TableHead><TableHead>AI Diagnosis</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {metrics.map((m) => (
                    <React.Fragment key={m.id}>
                      <TableRow data-testid="product-metric-row" className="cursor-pointer" onClick={() => setExpandedDiag(expandedDiag === m.id ? null : m.id)}>
                        <TableCell className="font-mono text-sm tabular-nums">{m.date}</TableCell>
                        <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(m.spend)}</TableCell>
                        <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(m.revenue)}</TableCell>
                        <TableCell className={cn('text-right font-mono text-sm tabular-nums', roasTone(m.computed?.roas, m.spend))}>{fmtRoas(m.computed?.roas)}</TableCell>
                        <TableCell className={cn('text-right font-mono text-sm tabular-nums',
                          m.computed?.cpa != null && m.computed?.break_even_cpa > 0 && (m.computed.cpa <= m.computed.break_even_cpa ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'))}>{m.computed?.cpa != null ? fmtMoney(m.computed.cpa) : '—'}</TableCell>
                        <TableCell className={cn('text-right font-mono text-sm tabular-nums', m.computed?.profit > 0 ? 'text-emerald-600 dark:text-emerald-400' : m.computed?.profit < 0 ? 'text-rose-600 dark:text-rose-400' : '')}>{fmtMoney(m.computed?.profit)}</TableCell>
                        <TableCell>
                          {m.diagnosis_status === 'pending' ? <span className="flex items-center gap-1 text-xs text-muted-foreground"><Spinner className="h-3 w-3" />running</span>
                            : m.ai_diagnosis ? <span className="flex items-center gap-1 text-xs"><ChevronDown className="h-3 w-3" />{m.ai_diagnosis.overall_health}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                      {expandedDiag === m.id && m.ai_diagnosis && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/30">
                            <div className="space-y-2 p-2 text-sm">
                              {m.signal && <p className="rounded bg-amber-500/10 p-2 text-xs font-medium text-amber-700 dark:text-amber-400">{m.signal}</p>}
                              <p><span className="font-semibold">Call:</span> {m.ai_diagnosis.kill_or_continue} — {m.ai_diagnosis.summary}</p>
                              <p><span className="font-semibold">Bottleneck:</span> {m.ai_diagnosis.bottleneck}</p>
                              <ul className="list-disc space-y-1 pl-5">
                                {(m.ai_diagnosis.actions || []).map((a) => <li key={a.action}><span className="font-medium">{a.action}</span> — <span className="text-muted-foreground">{a.why}</span></li>)}
                              </ul>
                              {m.ai_diagnosis.missing_data?.length > 0 && (
                                <p className="text-xs text-muted-foreground">Missing data: {m.ai_diagnosis.missing_data.join(', ')}</p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ---- TASKS ---- */}
        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Input data-testid="product-task-input" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} placeholder="Add a task for this product..." />
            <Button data-testid="product-task-add-button" onClick={addTask}><Plus className="h-4 w-4" /></Button>
          </div>
          {tasks.length === 0 ? (
            <EmptyState testId="product-tasks-empty" icon={ListTodo} title="No tasks for this product" subtitle="Qualifying the product auto-creates starter tasks, or add your own above." />
          ) : (
            <Card><CardContent className="divide-y p-0">
              {tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Checkbox checked={t.done} onCheckedChange={() => toggleTask(t)} />
                  <span className={cn('flex-1 text-sm', t.done && 'text-muted-foreground line-through')}>{t.title}</span>
                  <span className="text-xs text-muted-foreground">{t.category}</span>
                </div>
              ))}
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ---- NOTES ---- */}
        <TabsContent value="notes" className="mt-4 space-y-3">
          <Textarea data-testid="product-notes-textarea" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={10}
            placeholder="Research notes, supplier conversations, kill reasons, lessons learned..." />
          <Button data-testid="product-notes-save-button" onClick={saveNotes}>Save Notes</Button>
          {product.description && (
            <Card><CardContent className="p-4 text-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</p>
              <p className="mt-1 whitespace-pre-wrap">{product.description}</p>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="product-edit-name-input" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={editForm.description || ''} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" rows={3} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Source $</Label><Input data-testid="product-edit-source-input" type="number" step="0.01" value={editForm.source_price} onChange={(e) => setEditForm((f) => ({ ...f, source_price: e.target.value }))} className="mt-1" /></div>
              <div><Label>Sell $</Label><Input data-testid="product-edit-sell-input" type="number" step="0.01" value={editForm.sell_price} onChange={(e) => setEditForm((f) => ({ ...f, sell_price: e.target.value }))} className="mt-1" /></div>
              <div><Label>Shipping $</Label><Input type="number" step="0.01" value={editForm.shipping_cost} onChange={(e) => setEditForm((f) => ({ ...f, shipping_cost: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Supplier URL</Label><Input value={editForm.supplier_url || ''} onChange={(e) => setEditForm((f) => ({ ...f, supplier_url: e.target.value }))} className="mt-1" /></div>
            <div><Label>Store product URL</Label><Input value={editForm.product_url || ''} onChange={(e) => setEditForm((f) => ({ ...f, product_url: e.target.value }))} className="mt-1" /></div>
            <div><Label>Image URL</Label><Input value={editForm.image_url || ''} onChange={(e) => setEditForm((f) => ({ ...f, image_url: e.target.value }))} className="mt-1" /></div>
            <Button data-testid="product-edit-save-button" onClick={saveEdit} className="w-full">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreativeDialog open={creativeOpen} onOpenChange={setCreativeOpen} creative={editCreative} products={[product]} defaultProductId={product.id} onSaved={load} />
      <MetricDialog open={metricOpen} onOpenChange={setMetricOpen} products={[product]} defaultProductId={product.id} onSaved={load} />
    </div>
  );
}
